import {useState, useEffect, useContext} from 'react';
import './QuestionImageUpload.scss';
import {Button} from "../../Form/Button/Button";
import {I18nReactContext} from "../../../contexts/I18nContext";
import {uploadQuestionMediaAPI, deleteQuestionMediaAPI} from "../../../Services/api";
import {FileUploadBox} from "../../Smart/FileUploadBox/FileUploadBox";
import {ConfirmationModal} from "../../Smart/confirmationModal/confirmationModal";
import DeleteIcon from '@mui/icons-material/Delete';
import {buttonColors} from "../../Pickers/InputTheme";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";

const ALLOWED_TYPES = {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/gif': ['.gif'],
    'image/webp': ['.webp']
};
const ALLOWED_TYPES_FOR_NOTIFICATION = ['JPG', 'PNG', 'GIF', 'WEBP']
const MAX_SIZE = 10 * 1024 * 1024; // 10MB

/**
 * Component for uploading and managing images attached to questionnaire questions.
 * Handles upload, delete with confirmation modal, and displays preview.
 *
 * @param {object} props - Component props
 * @param {string} props.signDocId - The ID of the sign document (exam/survey)
 * @param {string} props.questionId - The ID of the question
 * @param {string} [props.initialImageUrl] - Initial image URL if image already exists
 * @param {Function} [props.onImageChange] - Callback when image is uploaded or deleted
 * @param {Function} [props.onLoadingChange] - Callback when loading state changes
 * @returns {JSX.Element|null} The upload component or null if required props missing
 */
export const QuestionImageUpload = ({ signDocId, questionId, initialImageUrl, onImageChange, onLoadingChange }) => {
    const {i18n} = useContext(I18nReactContext);
    const [imageUrl, setImageUrl] = useState(initialImageUrl);
    const [error, setError] = useState(null);
    const [resetUploadState, setResetUploadState] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    useEffect(() => {
        setImageUrl(initialImageUrl);
    }, [initialImageUrl]);

    // Notify parent when loading state changes
    useEffect(() => {
        onLoadingChange?.({ isLoading });
    }, [isLoading, onLoadingChange]);

    // Guard clause: Don't render if required props are missing
    if (!signDocId || !questionId) {
        return null;
    }

    /**
     * Handles file upload to the server
     * @param {object} params - Upload parameters
     * @param {File[]} params.files - Array of files to upload
     * @param {Function} params.onProgress - Progress callback function
     */
    const handleUploadFiles = async ({ files, onProgress }) => {
        if (!files || files.length === 0 || isLoading) return;

        const file = files[0];
        setError(null);
        setIsLoading(true);

        try {
            const result = await uploadQuestionMediaAPI({
                signDocId,
                questionId,
                file,
                progressHandler: onProgress
            });
            setImageUrl(result.signedUrl);
            setResetUploadState(false);
            onImageChange?.({ imageUrl: result.signedUrl });
        } catch (err) {
            const errorMessage = err?.errorMessage || 
                                 err?.data?.errorMessage || 
                                 err?.message ||
                                 i18n.translate('questionnaireQuestions.errors.uploadFailed');
            setError(errorMessage);
            setResetUploadState(true);
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Opens the delete confirmation modal
     */
    const openDeleteConfirmation = () => {
        setShowDeleteConfirmation(true);
    };

    /**
     * Closes the delete confirmation modal
     */
    const closeDeleteConfirmation = () => {
        setShowDeleteConfirmation(false);
    };

    /**
     * Deletes the image from S3 and database after confirmation
     */
    const handleDelete = async () => {
        if (isLoading) return;
        
        setError(null);
        setIsLoading(true);
        setShowDeleteConfirmation(false);

        try {
            await deleteQuestionMediaAPI({ signDocId, questionId });
            setImageUrl(null);
            setResetUploadState(true);
            onImageChange?.({ imageUrl: null });
        } catch (err) {
            const errorMessage = err?.errorMessage || 
                                 err?.data?.errorMessage || 
                                 err?.message ||
                                 i18n.translate('questionnaireQuestions.errors.deleteFailed');
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    if (imageUrl) {
        return (
            <div className="question-image-preview">
                <img src={imageUrl} alt="Question" className="preview-image" />
                <div className="preview-actions">
                    <Button
                        onClick={openDeleteConfirmation}
                        color={buttonColors.GREY}
                        size="small"
                        icon={<DeleteIcon />}
                        text={i18n.translate('common.remove')}
                        className="delete-button"
                        disabled={isLoading}
                    />
                </div>
                {error && (
                    <div className="upload-error">{error}</div>
                )}
                
                <ConfirmationModal
                    confirmationModalOpen={showDeleteConfirmation}
                    onCloseConfirmationModal={closeDeleteConfirmation}
                    title={i18n.translate('questionnaireQuestions.deleteImageTitle')}
                    message={i18n.translate('questionnaireQuestions.confirmDeleteImage')}
                    onConfirm={handleDelete}
                    onCancel={closeDeleteConfirmation}
                    isLoading={isLoading}
                />
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="question-image-upload loading">
                <LoadingSpinner local />
            </div>
        );
    }

    return (
        <div className="question-image-upload">
            <FileUploadBox
                accept={ALLOWED_TYPES}
                maxFiles={1}
                maxSize={MAX_SIZE}
                uploadFiles={handleUploadFiles}
                supportedFileTypesForNotifications={ALLOWED_TYPES_FOR_NOTIFICATION}
                resetUploadState={resetUploadState}
            >
                <div className="upload-content">
                    <p className="upload-text">
                        {i18n.translate('questionnaireQuestions.dropImageOrClick')}
                    </p>
                </div>
            </FileUploadBox>

            {error && (
                <div className="upload-error">{error}</div>
            )}
        </div>
    );
};

